import React from 'react';
import type { BoxProps } from '@withjoy/joykit';
import { Accordion, Box, Flex } from '@withjoy/joykit';
import { StyledDepartmentsContainer, StyledNavLink } from './DepartmentsTree.styles';
import { useCatalogRouting } from '../../useCatalogRouting';
import type { CatalogDepartment, CatalogDepartmentCategory } from '../../state/Catalog';
import { useCatalogDepartments } from '../../hooks/useCatalogDepartments/useCatalogDepartments';
import { SkeletonText } from '@shared/components/Skeleton';
import type { NavLinkProps } from '@react-router';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';

const NavLink = (props: BoxProps & NavLinkProps) => {
  return <StyledNavLink {...props} />;
};

export interface DepartmentsTreeProps {
  onDepartmentClick?: (dep: CatalogDepartment) => void;
  onCategoryClick?: (cat: CatalogDepartmentCategory) => void;
}
type FetchedDepartmentsTreeProps = DepartmentsTreeProps & { departments: CatalogDepartment[] };

const MobileOrTabletDepartmentsTree = ({ departments, onCategoryClick, onDepartmentClick }: FetchedDepartmentsTreeProps) => {
  const { getToCategoryPath } = useCatalogRouting();
  return (
    <Accordion
      listStyle={'none'}
      width="100%"
    >
      {departments.map(dep => {
        return (
          <Accordion.Item key={dep.id}>
            <Accordion.Button
              width="100%"
              paddingY={4}
            >
              <NavLink
                typographyVariant="hed1"
                to={getToCategoryPath(dep.publicUrlPath)}
                onClick={() => {
                  onDepartmentClick?.(dep);
                }}
              >
                {dep.title}
              </NavLink>
              <Accordion.Icon style={{ marginLeft: 'auto' }} />
            </Accordion.Button>
            <Accordion.Panel
              as="ul"
              paddingLeft={6}
              paddingTop={4}
            >
              {dep.children.map(cat => {
                return (
                  <Box
                    as="li"
                    key={cat.id}
                  >
                    <NavLink
                      typographyVariant="body1"
                      marginBottom={5}
                      to={getToCategoryPath(cat.publicUrlPath)}
                      onClick={() => {
                        onCategoryClick?.(cat);
                      }}
                    >
                      {cat.title}
                    </NavLink>
                  </Box>
                );
              })}
            </Accordion.Panel>
          </Accordion.Item>
        );
        return <></>;
      })}
    </Accordion>
  );
};

const DesktopDepartmentsTree = ({ departments, onCategoryClick, onDepartmentClick }: FetchedDepartmentsTreeProps) => {
  const { getToCategoryPath } = useCatalogRouting();
  return (
    <StyledDepartmentsContainer>
      {departments.map(dep => {
        return (
          <Box key={dep.id}>
            <NavLink
              typographyVariant="hed1"
              to={getToCategoryPath(dep.publicUrlPath)}
              onClick={() => {
                onDepartmentClick?.(dep);
              }}
            >
              {dep.title}
            </NavLink>
            <Box
              as="ul"
              marginTop={4}
            >
              {dep.children.slice(0, 9).map(cat => {
                return (
                  <Box
                    as="li"
                    key={cat.id}
                  >
                    <NavLink
                      typographyVariant="body1"
                      marginBottom={3}
                      to={getToCategoryPath(cat.publicUrlPath)}
                      onClick={() => {
                        onCategoryClick?.(cat);
                      }}
                    >
                      {cat.title}
                    </NavLink>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </StyledDepartmentsContainer>
  );
};

const Root = (props: FetchedDepartmentsTreeProps) => {
  const isMobileOrTablet = useIsMobileOrTablet();
  return isMobileOrTablet ? <MobileOrTabletDepartmentsTree {...props} /> : <DesktopDepartmentsTree {...props} />;
};

const DepartmentSkeleton = () => {
  return (
    <Box width={'100%'}>
      <SkeletonText
        skeletonHeight={5}
        rows={1}
      />
      <SkeletonText
        skeletonHeight={4}
        rows={6}
      />
    </Box>
  );
};

const DepartmentsTreeSkeleton = () => {
  return (
    <Flex
      gap={6}
      flexDirection={['column', 'column', 'column', 'row']}
    >
      <DepartmentSkeleton />
      <DepartmentSkeleton />
      <DepartmentSkeleton />
    </Flex>
  );
};

export const DepartmentsTree = (props: DepartmentsTreeProps) => {
  const { departments, loading: loadingDepartments } = useCatalogDepartments();
  const filteredDepartments: CatalogDepartment[] = departments.filter(dep => !dep.__IS_BRANDS__);
  return loadingDepartments ? (
    <DepartmentsTreeSkeleton />
  ) : (
    <Root
      departments={filteredDepartments}
      {...props}
    />
  );
};
